import React, {Component} from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {PageModules} from 'Frontend/components/modules';
import PageLayout from 'Frontend/components/PageLayout';
import {Page, PageConfig, Redirect} from 'Server/models';

import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import dbConnect from 'Server/utils/dbConnect';
import {parser, populateFooterItems, populateMenuItems} from 'Server/utils/pagePopulations';

/**
 * ContentPages Page class.
 *
 * @export
 * @class ContentPages
 * @extends {Component}
 */
export default class ContentPages extends Component {
    static propTypes = {
        footer: PropTypes.object,
        menu: PropTypes.object,
        page: PropTypes.shape({
            modules: PropTypes.array,
            seoDescription: PropTypes.string,
            seoKeywordList: PropTypes.array,
            // eslint-disable-next-line react/boolean-prop-naming
            seoNoFollow: PropTypes.bool,
            // eslint-disable-next-line react/boolean-prop-naming
            seoNoIndex: PropTypes.bool,
            seoPageTitle: PropTypes.string
        })
    }

    static defaultProps = {
        footer: null,
        menu: null,
        page: {
            modules: [],
            seoDescription: '',
            seoKeywordList: [],
            seoNoFollow: false,
            seoNoIndex: false,
            seoPageTitle: ''
        }
    }

    /**
     * Gets the layout for this page.
     *
     * @static
     * @param {Object} router        The router object from nextjs.
     * @param {Object} pageProps     All page props given from server.
     * @param {Object} PageComponent The page component to render.
     *
     * @returns {JSX} The complete page.
     * @memberof ContentPages
     */
    static getLayout(router, pageProps, PageComponent) {
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <PageLayout router={router} {...pageProps}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <PageComponent {...pageProps} />
            </PageLayout>
        );
    }

    /**
     * Renders the Component.
     *
     * @returns {Component} React Component.
     * @memberof ContentPages
     */
    render() {
        const {page} = this.props;

        return (
            <ContentWrapper>
                {page.modules.map(module => {
                    if (PageModules[module.type]) {
                        const Comp = PageModules[module.type];

                        // eslint-disable-next-line react/jsx-props-no-spreading
                        return <Comp key={module.name} {...module} />;
                    }

                    return null;
                })}
            </ContentWrapper>
        );
    }
}

const ContentWrapper = styled.div`
    flex: 1 1 auto;
    margin-top: calc(${clampCss(56, 'm')} + 35px);

    ${MEDIA.DESKTOP.UP} {
        margin-top: calc(max(124px, ${pxToVw(124, 'd')}) + 56px);
    }
`;

/**
 * Generates an path list for all dynamic pages.
 *
 * @export
 * @returns {Object} An path object used to determine static optimisation.
 */
export async function getStaticPaths() {
    return {
        fallback: 'blocking',
        paths: []
    };
}

/**
 * Gets static props needed for the actual page.
 *
 * @export
 * @param {Object} ctx    The context in which this function is called.
 * @param {Object} params The params given for this page.
 * @returns {Object} An props object or an redirect.
 */
export async function getStaticProps({params}) {
    const path = params.page.join('/');
    const redirect = await Redirect.findOne({from: path}).select('to');

    let ret = {revalidate: 2};

    if (redirect) {
        ret = Object.assign(ret, {
            redirect: {
                destination: `/${redirect.to}`,
                permanent: true
            }
        });
    }

    if (!redirect) {
        await dbConnect();
        const menu = await PageConfig.findOne({type: 'Menu'});
        const page = await Page.findOne({seoSlug: path})
            .select('-createdAt -updatedAt');
        const footer = await PageConfig.findOne({type: 'Footer'})
            .populate('partner')
            .populate('socialMedia.icon');

        if (page) {
            await Promise.all(page.modules.map(async part => {
                if (parser[part.type]) {
                    await parser[part.type](part);
                }
            }));

            await populateMenuItems(menu.mainMenu);
            await populateFooterItems(footer.copyright);

            const menuJson = JSON.stringify(menu.toObject());
            const pageJson = JSON.stringify(page.toObject());
            const footerJson = JSON.stringify(footer.toObject());
            const menuProp = JSON.parse(menuJson);

            menuProp.active = path;

            ret = Object.assign(ret, {
                props: {
                    footer: JSON.parse(footerJson),
                    menu: menuProp,
                    page: JSON.parse(pageJson)
                }
            });
        } else {
            ret = Object.assign(ret, {notFound: true});
        }
    }

    return ret;
}